import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/Icon";
import { TextField, TextAreaField, SelectField, Field } from "@/components/FormKit";
import { Input } from "@/components/ui/input";
import { SuccessScreen } from "@/pages/BookRide";
import { PageHero } from "@/components/PublicLayout";
import { COUNTIES } from "@/lib/data";

const empty = {
  full_name: "", phone: "", email: "", address: "", county: "",
  positions: [], employment_preference: "", availability: "",
  years_experience: "", license_status: "",
  wheelchair_experience: "", transportation_availability: "",
  driving_experience: "", age_21: "", willing_screening: "",
  odp_experience: "", certifications: [], additional_info: "",
  resume_file_id: null, resume_filename: null, acknowledged: false,
};
const CERTS = ["PA Criminal Background Check", "Child Abuse Clearance", "FBI Fingerprints", "CPR/First Aid", "Medication Administration (if applicable)", "Other", "None yet"];

export default function JobApplication() {
  const jobParam = new URLSearchParams(window.location.search).get("job") || "";
  const [jobs, setJobs] = useState([]);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setE = (k) => (e) => set(k)(e.target.value);

  useEffect(() => {
    api.get("/job-openings").then((r) => {
      setJobs(r.data);
      if (jobParam) {
        const j = r.data.find((x) => x.id === jobParam);
        if (j) setForm((f) => ({ ...f, positions: [j.title] }));
      }
    }).catch(() => {});
  }, [jobParam]);

  const byCat = useMemo(() => ({
    Transportation: jobs.filter((j) => j.category === "Transportation"),
    ODP: jobs.filter((j) => j.category === "ODP"),
  }), [jobs]);

  const selectedCats = useMemo(() => {
    const cats = new Set();
    form.positions.forEach((title) => {
      const j = jobs.find((x) => x.title === title);
      if (j) cats.add(j.category);
    });
    return cats;
  }, [form.positions, jobs]);
  const showTransport = selectedCats.has("Transportation");
  const showOdp = selectedCats.has("ODP");

  const togglePosition = (title) => setForm((f) => ({
    ...f,
    positions: f.positions.includes(title) ? f.positions.filter((p) => p !== title) : [...f.positions, title],
  }));
  const toggleCert = (c) => setForm((f) => ({
    ...f,
    certifications: (f.certifications || []).includes(c) ? f.certifications.filter((x) => x !== c) : [...(f.certifications || []), c],
  }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload-resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, resume_file_id: data.file_id, resume_filename: data.filename }));
      toast.success("Resume uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email) { toast.error("Please complete your name, phone, and email."); return; }
    if (form.positions.length === 0) { toast.error("Please select at least one position."); return; }
    if (!form.acknowledged) { toast.error("Please confirm the acknowledgment to submit."); return; }
    const primary = jobs.find((x) => x.title === form.positions[0]);
    const category = showTransport && showOdp ? "Transportation & ODP" : showOdp ? "ODP" : "Transportation";
    const payload = {
      ...form,
      position: form.positions[0] || "",
      category,
      certifications: Array.isArray(form.certifications) ? form.certifications.join(", ") : (form.certifications || ""),
      job_opening_id: primary?.id || jobParam || "",
      job_title: form.positions.join(", "),
    };
    setLoading(true);
    try {
      await api.post("/applications", payload);
      setDone(true);
      toast.success("Application submitted!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally { setLoading(false); }
  };

  if (done) return <SuccessScreen title="Application Received" message="Thank you for applying to CareDeserved! Our team will review your application and reach out if there's a fit." />;

  return (
    <>
      <PageHero eyebrow="Employment Application" title="Apply to CareDeserved"
        subtitle="One smart application for every role. Select the position(s) you're interested in and tell us about yourself — you can apply for more than one." />
      <section className="bg-white py-14 sm:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <form onSubmit={submit} data-testid="application-form" className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
            {/* Positions */}
            <h2 className="font-heading text-lg font-bold text-slate-900">Position(s) You're Applying For</h2>
            <p className="mt-1 text-sm text-slate-500">Select one or more. The form adjusts to what you choose.</p>
            <div className="mt-4 grid gap-6 sm:grid-cols-2">
              {["Transportation", "ODP"].map((cat) => (
                <div key={cat}>
                  <p className="text-xs font-bold uppercase tracking-wide text-brand-purple">{cat === "ODP" ? "ODP / Human Services" : "Transportation / NEMT"}</p>
                  <div className="mt-2 space-y-2">
                    {byCat[cat].map((j) => (
                      <label key={j.id} className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 hover:border-brand-purple/40">
                        <input type="checkbox" data-testid={`pos-${j.id}`} checked={form.positions.includes(j.title)}
                          onChange={() => togglePosition(j.title)} className="h-4 w-4 rounded border-slate-300 accent-brand-purple" />
                        <span className="text-sm text-slate-700">{j.title}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              <TextField label="Full Name" required testid="app-name" wrapClass="sm:col-span-2" value={form.full_name} onChange={setE("full_name")} />
              <TextField label="Phone" required testid="app-phone" type="tel" value={form.phone} onChange={setE("phone")} />
              <TextField label="Email" required testid="app-email" type="email" value={form.email} onChange={setE("email")} />
              <TextField label="Street Address" testid="app-address" value={form.address} onChange={setE("address")} />
              <SelectField label="County You Live In" testid="app-county" value={form.county} onValueChange={set("county")}
                placeholder="Select county" options={[...COUNTIES, "Other / Not listed"]} />
              <SelectField label="Full-Time / Part-Time Preference" testid="app-preference" value={form.employment_preference} onValueChange={set("employment_preference")}
                placeholder="Select preference" options={["Full-Time", "Part-Time", "PRN / As-Needed", "Either"]} />
              <TextField label="Availability" testid="app-availability" value={form.availability} onChange={setE("availability")} placeholder="e.g. weekdays, evenings, weekends" />
              <TextAreaField label="Relevant Work Experience" testid="app-experience" wrapClass="sm:col-span-2" value={form.years_experience} onChange={setE("years_experience")} placeholder="Briefly describe your relevant work experience." />

              {showTransport && (
                <>
                  <div className="sm:col-span-2 mt-2 border-t border-slate-100 pt-4">
                    <p className="font-heading text-sm font-bold text-brand-teal">Transportation / Driving Details</p>
                  </div>
                  <SelectField label="Driver's License Status" testid="app-license" value={form.license_status} onValueChange={set("license_status")}
                    placeholder="Select status" options={["Valid PA License", "Valid Out-of-State License", "CDL", "None"]} />
                  <TextField label="Driving / NEMT Experience" testid="app-driving-exp" value={form.driving_experience} onChange={setE("driving_experience")} placeholder="e.g. 3 years driving, some NEMT" />
                  <SelectField label="Wheelchair Transportation Experience" testid="app-wheelchair" value={form.wheelchair_experience} onValueChange={set("wheelchair_experience")}
                    placeholder="Select" options={["Yes", "No", "Some"]} />
                  <SelectField label="Reliable Transportation to Work" testid="app-transport" value={form.transportation_availability} onValueChange={set("transportation_availability")}
                    placeholder="Select" options={["Yes", "No"]} />
                  <SelectField label="Are you at least 21 years old?" testid="app-age21" value={form.age_21} onValueChange={set("age_21")}
                    placeholder="Select" options={["Yes", "No"]} />
                  <SelectField label="Willing to complete required background checks, drug screening, training & credentialing?" testid="app-willing" wrapClass="sm:col-span-2" value={form.willing_screening} onValueChange={set("willing_screening")}
                    placeholder="Select" options={["Yes", "No"]} />
                </>
              )}

              {showOdp && (
                <>
                  <div className="sm:col-span-2 mt-2 border-t border-slate-100 pt-4">
                    <p className="font-heading text-sm font-bold text-brand-purple">ODP / Human Services Details</p>
                  </div>
                  <TextAreaField label="Relevant ODP / DSP / Caregiving Experience" testid="app-odp-exp" wrapClass="sm:col-span-2" value={form.odp_experience} onChange={setE("odp_experience")} placeholder="Describe any DSP, caregiving, or community support experience." />
                  <Field label="Certifications & Clearances Held (select all that apply)" className="sm:col-span-2">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {CERTS.map((c) => (
                        <label key={c} className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 hover:border-brand-purple/40">
                          <input type="checkbox" data-testid={`app-cert-${c.split(" ")[0].toLowerCase()}`} checked={(form.certifications || []).includes(c)} onChange={() => toggleCert(c)} className="h-4 w-4 rounded border-slate-300 accent-brand-purple" />
                          <span className="text-sm text-slate-700">{c}</span>
                        </label>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-slate-500">You can still apply if you don't have every clearance yet — we'll help determine eligibility during screening.</p>
                  </Field>
                </>
              )}

              <Field label="Resume Upload" className="sm:col-span-2">
                <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-slate-300 bg-brand-bg px-4 py-4 hover:border-brand-purple">
                  <Icon name="FirstAid" size={22} className="text-brand-purple" />
                  <span className="text-sm text-slate-600">
                    {uploading ? "Uploading…" : form.resume_filename ? `Uploaded: ${form.resume_filename}` : "Upload PDF, DOC, DOCX or TXT (max 10MB)"}
                  </span>
                  <Input type="file" accept=".pdf,.doc,.docx,.txt" onChange={onFile} data-testid="app-resume" className="hidden" />
                </label>
              </Field>

              <TextAreaField label="Anything Else You'd Like Us to Know" testid="app-notes" wrapClass="sm:col-span-2" value={form.additional_info} onChange={setE("additional_info")} />
            </div>

            <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-xl bg-brand-bg p-4">
              <input type="checkbox" data-testid="app-ack" checked={form.acknowledged}
                onChange={(e) => set("acknowledged")(e.target.checked)} className="mt-0.5 h-5 w-5 rounded border-slate-300 accent-brand-purple" />
              <span className="text-sm text-slate-600">I certify the information provided is accurate and understand that employment is contingent on successfully completing CareDeserved's required screenings, clearances, and training. Please do not include sensitive information such as your SSN on this form.</span>
            </label>

            <Button type="submit" disabled={loading || uploading} data-testid="app-submit"
              className="mt-6 h-14 w-full rounded-full bg-brand-purple text-base font-semibold text-white hover:bg-brand-purple2">
              {loading ? "Submitting…" : "Submit Application"}
            </Button>
          </form>
        </div>
      </section>
    </>
  );
}
